import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Issue, IssueComment, Priority, Profile, Status } from '../types';
import ProgressTracker from '../components/ProgressTracker';
import ImageUploader from '../components/ImageUploader';
import RichTextEditor from '../components/RichTextEditor';
import CloseIssueModal, { CloseProof } from '../components/CloseIssueModal';

const priorityConfig: Record<string, { className: string }> = {
  High:   { className: 'priority-high' },
  Medium: { className: 'priority-medium' },
  Low:    { className: 'priority-low' },
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 1)  return 'just now';
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days  < 7)  return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function IssueDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, role } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [issue,         setIssue]         = useState<Issue | null>(null);
  const [loading,       setLoading]       = useState(true);
  const [editing,       setEditing]       = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [lightboxSrc,   setLightboxSrc]   = useState<string | null>(null);

  // Close proof modal
  type PendingClose = 'quick' | 'save';
  const [pendingClose, setPendingClose] = useState<PendingClose | null>(null);

  // Edit state
  const [editTitle,       setEditTitle]       = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority,    setEditPriority]    = useState<Priority>('Medium');
  const [editStatus,      setEditStatus]      = useState<Status>('Open');
  const [editImages,      setEditImages]      = useState<File[]>([]);
  const [removedImages,   setRemovedImages]   = useState<string[]>([]);
  const [editComment,     setEditComment]     = useState('');

  // Comments
  const [comments,        setComments]        = useState<IssueComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [actionHint,      setActionHint]      = useState<string | null>(null);

  // Team members for @mention
  const [members, setMembers] = useState<Profile[]>([]);

  const fetchIssue = useCallback(async () => {
    try {
      const iss = await api.get<Issue>('/api/issues/' + id);
      setIssue(iss);
      setEditTitle(iss.title);
      setEditDescription(iss.description ?? '');
      setEditPriority(iss.priority);
      setEditStatus(iss.status);
    } catch {
      showToast('Issue not found.', 'error');
      navigate('/');
    }
    setLoading(false);
  }, [id, navigate, showToast]);

  const fetchComments = useCallback(async () => {
    if (!id) return;
    setCommentsLoading(true);
    try {
      const rows = await api.get<IssueComment[]>('/api/issues/' + id + '/comments');
      setComments(rows ?? []);
    } catch {
      setComments([]);
    }
    setCommentsLoading(false);
  }, [id]);

  // Fetch company members for @mention once issue is loaded
  useEffect(() => {
    if (!issue?.company_id) return;
    api.get<Profile[]>('/api/users')
      .then(data => setMembers(data ?? []))
      .catch(() => setMembers([]));
  }, [issue?.company_id]);

  useEffect(() => { fetchIssue(); },   [fetchIssue]);
  useEffect(() => { fetchComments(); }, [fetchComments]);

  const postComment = async (content: string) => {
    if (!content.trim() || !id || !user) return;
    try {
      await api.post('/api/issues/' + id + '/comments', { content: content.trim() });
      fetchComments();
    } catch {
      showToast('Failed to post comment.', 'error');
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      await api.delete('/api/issues/' + id + '/comments/' + commentId);
      fetchComments();
    } catch {
      showToast('Failed to delete comment.', 'error');
    }
  };

  const startEdit = () => {
    if (!issue) return;
    setEditTitle(issue.title);
    setEditDescription(issue.description ?? '');
    setEditPriority(issue.priority);
    setEditStatus(issue.status);
    setEditImages([]);
    setRemovedImages([]);
    setEditComment('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditImages([]);
    setRemovedImages([]);
    setEditComment('');
  };

  const uploadImages = async (files: File[]): Promise<string[]> => {
    const urls: string[] = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { url } = await api.upload<{ url: string }>('/api/upload', formData);
        urls.push(url);
      } catch {
        showToast(`Failed to upload ${file.name}`, 'error');
      }
    }
    return urls;
  };

  // ── Core save (called directly OR after close-proof modal) ──
  const performSave = async (closeProof?: CloseProof) => {
    if (!issue || !editTitle.trim()) { showToast('Title is required.', 'error'); return; }
    setSaving(true);

    const newImageUrls = editImages.length > 0 ? await uploadImages(editImages) : [];
    const existingImgs = (issue.images ?? []).filter(u => !removedImages.includes(u));
    const finalImages  = [...existingImgs, ...newImageUrls];

    const updatePayload: Record<string, unknown> = {
      title:       editTitle.trim(),
      description: editDescription,
      priority:    editPriority,
      status:      editStatus,
      images:      finalImages,
      updated_at:  new Date().toISOString(),
    };

    if (closeProof && editStatus === 'Closed' && issue.status !== 'Closed') {
      updatePayload.close_reason      = closeProof.reason || null;
      updatePayload.close_proof_image = closeProof.imageUrl || null;
      updatePayload.closed_at         = new Date().toISOString();
      updatePayload.closed_by         = user!.id;
    }

    try {
      const updated = await api.patch<Issue>('/api/issues/' + issue.id, updatePayload);
      if (editComment.trim()) await postComment(editComment.trim());
      setIssue(updated);
      setEditing(false);
      setEditImages([]);
      setRemovedImages([]);
      setEditComment('');
      showToast('Issue updated!', 'success');
      setActionHint('Issue updated — add a note about your changes');
    } catch (err: any) {
      showToast(err?.message || 'Failed to update issue.', 'error');
    }
    setSaving(false);
  };

  // ── Quick status change (view mode) ──
  const performQuickStatus = async (newStatus: Status, closeProof?: CloseProof) => {
    if (!issue) return;
    const updatePayload: Record<string, unknown> = {
      status:     newStatus,
      updated_at: new Date().toISOString(),
    };
    if (closeProof && newStatus === 'Closed') {
      updatePayload.close_reason      = closeProof.reason || null;
      updatePayload.close_proof_image = closeProof.imageUrl || null;
      updatePayload.closed_at         = new Date().toISOString();
      updatePayload.closed_by         = user!.id;
    }
    try {
      const updated = await api.patch<Issue>('/api/issues/' + issue.id, updatePayload);
      setIssue(updated);
      showToast(`Status changed to "${newStatus}"`, 'success');
      setActionHint(`Status changed to "${newStatus}" — add a note (optional)`);
    } catch {
      showToast('Failed to update status.', 'error');
    }
  };

  // ── Public handlers ──
  const handleSave = () => {
    // Intercept: editing → Closed (and wasn't already Closed)
    if (editStatus === 'Closed' && issue?.status !== 'Closed') {
      setPendingClose('save');
      return;
    }
    performSave();
  };

  const handleQuickStatusChange = (newStatus: Status) => {
    if (!issue) return;
    if (newStatus === 'Closed' && issue.status !== 'Closed') {
      setPendingClose('quick');
      return;
    }
    performQuickStatus(newStatus);
  };

  const handleCloseConfirm = async (proof: CloseProof) => {
    const trigger = pendingClose;
    setPendingClose(null);
    if (trigger === 'save') {
      await performSave(proof);
    } else {
      await performQuickStatus('Closed', proof);
    }
  };

  const handleCloseCancel = () => setPendingClose(null);

  const handleDelete = async () => {
    if (!issue) return;
    setDeleting(true);
    try {
      await api.delete('/api/issues/' + issue.id);
      showToast('Issue deleted.', 'info');
      navigate(issue.project_id ? `/projects/${issue.project_id}` : '/');
    } catch (err: any) {
      showToast(err?.message || 'Failed to delete issue.', 'error');
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-detail">
          <div className="skeleton skeleton-title" />
          <div className="skeleton skeleton-text" />
          <div className="skeleton skeleton-text short" />
        </div>
      </div>
    );
  }

  if (!issue) return null;

  const canEdit   = role === 'admin' || user?.id === issue.assigned_developer_id;
  const canReview = role === 'tester' && (user?.id === issue.assigned_tester_id || issue.review_status === 'ready_for_review');
  const canDelete = role === 'admin';
  const existingImages = (issue.images ?? []).filter(u => !removedImages.includes(u));
  const priority = priorityConfig[issue.priority] ?? priorityConfig.Medium;

  // Closer name comes from embedded fields on the issue response
  const closerName = issue.closer_name || issue.closer_email || null;

  return (
    <div className="page">
      {/* Lightbox */}
      {lightboxSrc && (
        <div className="lightbox" onClick={() => setLightboxSrc(null)}>
          <button className="lightbox-close" onClick={() => setLightboxSrc(null)}>×</button>
          <img src={lightboxSrc} alt="Full size" className="lightbox-img" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Close Proof Modal */}
      <CloseIssueModal
        isOpen={pendingClose !== null}
        issueTitle={issue.title}
        onConfirm={handleCloseConfirm}
        onCancel={handleCloseCancel}
      />

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>

        {(canEdit || canDelete) && !editing && (
          <div className="page-actions">
            {canEdit && (
              <button className="btn btn-secondary" onClick={startEdit}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit
              </button>
            )}
            {canDelete && (
              !confirmDelete ? (
                <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                  </svg>
                  Delete
                </button>
              ) : (
                <div className="confirm-delete">
                  <span>Are you sure?</span>
                  <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
                    {deleting ? <span className="btn-spinner" /> : 'Yes, Delete'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
                </div>
              )
            )}
          </div>
        )}
      </div>

      <div className="detail-card">
        {editing ? (
          <div className="edit-form">
            <div className="form-group">
              <label className="form-label">Title <span className="required">*</span></label>
              <input type="text" className="form-input" value={editTitle}
                onChange={e => setEditTitle(e.target.value)} maxLength={200} />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <RichTextEditor value={editDescription} onChange={setEditDescription} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select className="form-select" value={editPriority}
                  onChange={e => setEditPriority(e.target.value as Priority)}>
                  <option value="High">🔴 High</option>
                  <option value="Medium">🟡 Medium</option>
                  <option value="Low">🟢 Low</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Progress</label>
              <ProgressTracker status={editStatus} onChange={setEditStatus} />
            </div>
            <div className="form-group">
              <label className="form-label">Attachments</label>
              <ImageUploader images={editImages} existingImageUrls={existingImages}
                onChange={setEditImages}
                onRemoveExisting={url => setRemovedImages(prev => [...prev, url])} />
            </div>
            <div className="form-group">
              <label className="form-label">
                Comment on your changes
                <span className="form-label-hint">(optional)</span>
              </label>
              <textarea className="form-textarea" rows={3}
                placeholder="Describe what you changed and why…"
                value={editComment} onChange={e => setEditComment(e.target.value)} />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={cancelEdit}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="btn-spinner" /> Saving…</> : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="detail-header">
              <div className="detail-badges">
                <span className={`priority-badge ${priority.className}`}>{issue.priority}</span>
              </div>
              <h1 className="detail-title">{issue.title}</h1>
              <p className="detail-meta">
                Created {formatDate(issue.created_at)}
                {issue.updated_at !== issue.created_at && (
                  <> &middot; Updated {formatDate(issue.updated_at)}</>
                )}
              </p>
            </div>

            <div className="detail-section">
              <h2 className="section-title">Progress</h2>
              <ProgressTracker status={issue.status}
                onChange={canEdit ? handleQuickStatusChange : undefined}
                readonly={!canEdit} />
            </div>

            {/* Resolution proof — shown when issue is closed and proof exists */}
            {issue.status === 'Closed' && (issue.close_reason || issue.close_proof_image) && (
              <div className="detail-section">
                <h2 className="section-title">Resolution</h2>
                <div className="resolution-box">
                  <div className="resolution-box-header">
                    <span className="resolution-closed-chip">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Closed
                    </span>
                    {issue.closed_at && (
                      <span className="resolution-date">{formatDate(issue.closed_at)}</span>
                    )}
                    {closerName && (
                      <span className="resolution-by">by {closerName}</span>
                    )}
                  </div>

                  {issue.close_reason && (
                    <p className="resolution-note">{issue.close_reason}</p>
                  )}

                  {issue.close_proof_image && (
                    <div className="resolution-proof-wrap">
                      <p className="resolution-proof-label">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                        Screenshot proof
                      </p>
                      <div
                        className="resolution-proof-img-wrap clickable"
                        onClick={() => setLightboxSrc(issue.close_proof_image!)}
                        title="Click to enlarge"
                      >
                        <img
                          src={issue.close_proof_image}
                          alt="Resolution proof screenshot"
                          className="resolution-proof-img"
                        />
                        <div className="preview-overlay">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {canReview && (
              <div className="detail-section">
                <h2 className="section-title">Tester Review</h2>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary"
                    onClick={() => setPendingClose('quick')}>
                    Approve & Close
                  </button>
                  <button className="btn btn-secondary"
                    onClick={() => { performQuickStatus('In Progress'); setActionHint('Requested rework — add a note (optional)'); }}>
                    Request Rework
                  </button>
                </div>
              </div>
            )}

            {issue.description && (
              <div className="detail-section">
                <h2 className="section-title">Description</h2>
                <div
                  className="detail-description rte-output"
                  dangerouslySetInnerHTML={{ __html: issue.description }}
                />
              </div>
            )}

            {issue.images && issue.images.length > 0 && (
              <div className="detail-section">
                <h2 className="section-title">Attachments ({issue.images.length})</h2>
                <div className="image-preview-grid">
                  {issue.images.map(url => (
                    <div key={url} className="image-preview-item clickable" onClick={() => setLightboxSrc(url)}>
                      <img src={url} alt="Attachment" className="preview-img" />
                      <div className="preview-overlay">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Comments section */}
      <CommentsSection
        comments={comments}
        loading={commentsLoading}
        currentUserId={user?.id ?? ''}
        isAdmin={role === 'admin'}
        actionHint={actionHint}
        onClearHint={() => setActionHint(null)}
        onPost={postComment}
        onDelete={deleteComment}
        members={members}
      />
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────
const ROLE_COLORS: Record<string, string> = {
  admin: '#1C9B82', developer: '#3b82f6', tester: '#f59e0b',
};

function renderContent(text: string) {
  return text.split(/(@[\w\s]+?)(?=\s|$|[^a-zA-Z0-9_\s])/g).map((part, i) =>
    part.startsWith('@')
      ? <span key={i} className="comment-mention">{part}</span>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

// ── CommentsSection ───────────────────────────────────────────
interface CommentsSectionProps {
  comments: IssueComment[];
  loading: boolean;
  currentUserId: string;
  isAdmin: boolean;
  actionHint: string | null;
  onClearHint: () => void;
  onPost: (content: string) => Promise<void>;
  onDelete: (id: string) => void;
  members: Profile[];
}

function CommentsSection({ comments, loading, currentUserId, isAdmin, actionHint, onClearHint, onPost, onDelete, members }: CommentsSectionProps) {
  const [text,    setText]    = useState('');
  const [posting, setPosting] = useState(false);

  const [mentionQuery,   setMentionQuery]   = useState<string | null>(null);
  const [mentionStart,   setMentionStart]   = useState(0);
  const [mentionIndex,   setMentionIndex]   = useState(0);
  const [mentionResults, setMentionResults] = useState<Profile[]>([]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (actionHint) { setText(''); textareaRef.current?.focus(); }
  }, [actionHint]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val    = e.target.value;
    const cursor = e.target.selectionStart ?? val.length;
    setText(val);

    const before = val.slice(0, cursor);
    const match  = before.match(/@([\w]*)$/);

    if (match) {
      const query = match[1].toLowerCase();
      setMentionQuery(match[1]);
      setMentionStart(cursor - match[0].length);
      setMentionIndex(0);
      setMentionResults(
        members.filter(m => {
          const name  = (m.full_name ?? '').toLowerCase();
          const email = m.email.toLowerCase();
          return name.includes(query) || email.includes(query);
        }).slice(0, 6)
      );
    } else {
      setMentionQuery(null);
      setMentionResults([]);
    }
  };

  const memberLabel = (m: Profile) =>
    m.full_name?.trim() || m.email.split('@')[0];

  const applyMention = (member: Profile) => {
    const label      = memberLabel(member);
    const cursor     = textareaRef.current?.selectionStart ?? (mentionStart + (mentionQuery?.length ?? 0) + 1);
    const before     = text.slice(0, mentionStart);
    const after      = text.slice(cursor);
    const newText    = `${before}@${label} ${after}`;
    setText(newText);
    setMentionQuery(null);
    setMentionResults([]);

    setTimeout(() => {
      const pos = mentionStart + label.length + 2;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionResults.length > 0) {
      if (e.key === 'ArrowDown')  { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionResults.length - 1)); return; }
      if (e.key === 'ArrowUp')    { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); applyMention(mentionResults[mentionIndex]); return; }
      if (e.key === 'Escape')     { setMentionQuery(null); setMentionResults([]); return; }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setPosting(true);
    await onPost(text);
    setText('');
    setPosting(false);
    onClearHint();
    setMentionQuery(null);
    setMentionResults([]);
  };

  const commentInitials = (c: IssueComment) =>
    (c.profiles?.full_name?.trim() || c.profiles?.email || '?').charAt(0).toUpperCase();

  const commentName = (c: IssueComment) =>
    c.profiles?.full_name?.trim() || c.profiles?.email?.split('@')[0] || 'Unknown';

  return (
    <div className="comments-section">
      <h2 className="comments-heading">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        </svg>
        Comments
        {comments.length > 0 && <span className="comments-count">{comments.length}</span>}
      </h2>

      <div className="comments-list">
        {loading ? (
          <div className="comments-loading">
            {[1,2].map(i => <div key={i} className="comment-skeleton" />)}
          </div>
        ) : comments.length === 0 ? (
          <p className="comments-empty">No comments yet. Be the first to leave one.</p>
        ) : (
          comments.map(c => (
            <div key={c.id} className="comment-item">
              <div className="comment-avatar">{commentInitials(c)}</div>
              <div className="comment-body">
                <div className="comment-meta">
                  <span className="comment-author">{commentName(c)}</span>
                  <span className="comment-time" title={formatDate(c.created_at)}>{timeAgo(c.created_at)}</span>
                </div>
                <p className="comment-content">{renderContent(c.content)}</p>
              </div>
              {(c.user_id === currentUserId || isAdmin) && (
                <button className="comment-delete-btn" title="Delete" onClick={() => onDelete(c.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                  </svg>
                </button>
              )}
            </div>
          ))
        )}
      </div>

      <div className="comment-form">
        {actionHint && (
          <div className="comment-action-hint">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {actionHint}
            <button type="button" className="hint-dismiss" onClick={onClearHint}>✕</button>
          </div>
        )}

        <div className="comment-input-wrap" ref={dropdownRef}>
          <textarea
            ref={textareaRef}
            className="comment-textarea"
            placeholder="Leave a comment… Type @ to mention a team member"
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            rows={3}
          />

          {mentionResults.length > 0 && (
            <div className="mention-dropdown">
              <div className="mention-dropdown-header">Team members</div>
              {mentionResults.map((m, i) => (
                <button
                  key={m.user_id}
                  type="button"
                  className={`mention-item${i === mentionIndex ? ' active' : ''}`}
                  onMouseDown={e => { e.preventDefault(); applyMention(m); }}
                  onMouseEnter={() => setMentionIndex(i)}
                >
                  <span className="mention-item-avatar" style={{ background: ROLE_COLORS[m.role] ?? '#6b7280' }}>
                    {(m.full_name?.trim() || m.email).charAt(0).toUpperCase()}
                  </span>
                  <span className="mention-item-info">
                    <span className="mention-item-name">{memberLabel(m)}</span>
                    <span className="mention-item-email">{m.email}</span>
                  </span>
                  <span className="mention-item-role" style={{ background: ROLE_COLORS[m.role] ?? '#6b7280' }}>
                    {m.role}
                  </span>
                </button>
              ))}
              <div className="mention-dropdown-footer">
                ↑↓ navigate &nbsp;·&nbsp; Enter to select &nbsp;·&nbsp; Esc to dismiss
              </div>
            </div>
          )}
        </div>

        <div className="comment-form-footer">
          <span className="comment-hint">Type @ to mention &nbsp;·&nbsp; Ctrl+Enter to submit</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {text.trim() && (
              <button type="button" className="btn btn-secondary btn-sm"
                onClick={() => { setText(''); onClearHint(); setMentionResults([]); }}>
                Discard
              </button>
            )}
            <button type="button" className="btn btn-primary btn-sm"
              disabled={posting || !text.trim()} onClick={handleSubmit}>
              {posting ? <><span className="btn-spinner" /> Posting…</> : 'Post Comment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
